import * as signalR from '@microsoft/signalr'
import { map, catchError } from 'rxjs/operators'
import { of } from 'rxjs'
import i18next from 'i18next'
import { toast } from 'react-toastify'
import logger from './Logger'
import * as R from 'ramda'

export const manageEpicResponse = (success, failure, errorStringId) => {
  return [
    map((response) => {
      if (!response) {
        toast.error(
          i18next.t(errorStringId, {
            error: i18next.t('common:errors.networkError'),
          }),
        )
        return failure({
          message: i18next.t('common:errors.networkError'),
          code: null,
        })
      } else if (response.status && response.status >= 400) {
        const errorText = `${response.status} - ${response.statusText}`
        toast.error(i18next.t(errorStringId, { error: errorText }))
        return failure({ message: errorText, code: response.status })
      } else if (!response.isSuccessful) {
        toast.error(
          i18next.t(errorStringId, { error: responseTextError(response) }),
        )
        return failure({ message: response.message, code: response.statusCode })
      }
      return success(response.data)
    }),
    catchError((err) => {
      toast.error(
        i18next.t(errorStringId, { error: responseFailedTextError(err) }),
      )
      return of(
        failure({
          message: i18next.t(errorStringId, {
            error: responseFailedTextError(err),
          }),
        }),
      )
    }),
  ]
}

export const manageMapEpicResponse = (
  id,
  success,
  failure,
  errorStringId,
  ignore404,
  log = false,
) => {
  return [
    map((response) => {
      if (log) {
        logger.debug('API response', response)
      }
      if (!response) {
        toast.error(
          i18next.t(errorStringId, {
            error: i18next.t('common:errors.networkError'),
          }),
        )
        return failure({
          id,
          message: i18next.t('common:errors.networkError'),
          code: null,
        })
      } else if (response.status && response.status >= 400) {
        const errorText = `${response.status} - ${response.statusText}`
        toast.error(i18next.t(errorStringId, { error: errorText }))
        return failure({ id, message: errorText, code: response.status })
      } else if (!response.isSuccessful) {
        (!ignore404 || response.statusCode !== 404) &&
          toast.error(
            i18next.t(errorStringId, { error: responseTextError(response) }),
          )
        return failure({
          id,
          message: response.message,
          code: response.statusCode,
        })
      }
      return success({ id, data: response.data })
    }),
    catchError((err) => {
      toast.error(
        i18next.t(errorStringId, { error: responseFailedTextError(err) }),
      )
      return of(
        failure({
          id,
          message: i18next.t(errorStringId, {
            error: responseFailedTextError(err),
          }),
        }),
      )
    }),
  ]
}

export const responseFailedTextError = (response) => response.message
export const responseTextError = (response) => {
  return response?.message || response?.data?.message || response?.data?.humanReadableMessage || response?.statusCode || i18next.t('common:errors.apiError')
}

export const isSuccessful = (response) => response?.isSuccessful

export const request = async (
  endpoint,
  args,
  successText = null,
  errorTextId = null,
  unwrap = false,
) => {
  try {
    const response = unwrap
      ? await endpoint(...args).unwrap()
      : await endpoint(...args)
    if (isSuccessful(response)) {
      successText && toast.success(successText)
      return { response, isSuccessful: true }
    } else {
      errorTextId &&
        toast[response?.statusCode === 409 ? 'warning' : 'error'](
          i18next.t(errorTextId, { error: responseTextError(response) }),
        )
      return { response, isSuccessful: false }
    }
  } catch (err) {
    logger.error('Request error: ', err)
    errorTextId &&
      toast.error(i18next.t(errorTextId, { error: responseTextError(err) }))
    return { response: await err.response, isSuccessful: false }
  }
}

export const setValidationErrors = (response, setErrors) => {
  if (response?.status === 400) {
    const errors = {}
    R.forEachObjIndexed(
      (err, field) => (errors[field] = err),
      response.data?.errors || {},
    )
    setErrors(errors)
  }
}

const fileNameRexp = /filename=([^;]+);/
export const download = async (
  endpoint,
  args,
  errorTextId = null,
  fileName = null,
) => {
  try {
    toast.info(i18next.t('common:ui.PreparingDownload'))
    const response = await endpoint(...args)
    if (!response) throw new Error('API error')
    if (!(response.data instanceof Blob)) return

    if (response.status !== 200) throw new Error(response.statusText)

    if (!fileName) {
      const m = response.headers['content-disposition']?.match(fileNameRexp)
      if (m && m.length > 1) fileName = m[1].replace(/^"/, '').replace(/"$/, '')
    }

    const blob = new Blob([response.data])
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = `${fileName}`
    link.click()
    toast.success(i18next.t('common:ui.DownloadStarted'))
    return { response, isSuccessful: true }
  } catch (err) {
    logger.error('Request error: ', err)
    errorTextId &&
      toast.error(i18next.t(errorTextId, { error: responseTextError(err) }))
    return { response: await err.response, isSuccessful: false }
  }
}

export const downloadMultiple = async (
  endpoint,
  args,
  errorTextId = null,
  throwError = true,
) => {
  try {
    const response = await endpoint(...args)

    if (response.isSuccessful) {
      toast.info(i18next.t('common:ui.PreparingZipArchive'))
      handleDownloadSignalrConnection(
        response.data.url,
        response.data.accessToken,
        errorTextId,
      )
    } else if (throwError) {
      throw new Error(responseTextError(response))
    }

    return { response, isSuccessful: response.isSuccessful }
  } catch (err) {
    logger.error('Request error: ', err)
    errorTextId && toast.error(i18next.t(errorTextId, { error: err }))
    return { response: await err.response, isSuccessful: false }
  }
}

export const downloadSingle = async (
  endpoint,
  args,
  errorTextId = null,
  throwError = true,
) => {
  try {
    const response = await endpoint(...args)

    if (response.url) {
      toast.info(i18next.t('common:ui.PreparingDownload'))
      handleDownloadSignalrConnection(
        response.url,
        response.accessToken,
        errorTextId,
      )
    } else if (throwError) {
      throw new Error(responseTextError(response))
    }

    return { response, isSuccessful: !!response.url }
  } catch (err) {
    logger.error('Request error: ', err)
    errorTextId && toast.error(i18next.t(errorTextId, { error: err }))
    return { response: await err.response, isSuccessful: false }
  }
}

const handleDownloadSignalrConnection = (url, accessToken, errorTextId) => {
  const connection = new signalR.HubConnectionBuilder()
    .withAutomaticReconnect([0, 1000, 1500, 1500, 2000])
    .withUrl(url, {
      accessTokenFactory: () => {
        return accessToken
      },
    })
    .configureLogging(signalR.LogLevel.Trace)
    .build()

  logger.info('Starting SignalR connection, multi file download')
  connection
    .start()
    .then(() => {
      const connectionId = connection.connectionId

      setTimeout(() => {
        if (connection && connection.connectionId === connectionId) {
          logger.warning(
            `Closing SignalR connection ${connectionId}, reason: Timeout`,
          )
          connection.stop()
        }
      }, 300000)
    })
    .catch((err) => {
      logger.error('SignalR connection error:', err)
      errorTextId &&
        toast.error(i18next.t(errorTextId, { error: err.toString() }))
    })

  connection.on('Ready', (data) => {
    logger.debug('Ready received', data)
    const downloadUrl = data.downloadUrl
    // const fileName = data.fileName

    if (downloadUrl?.length > 0) {
      // download(api.data.downloadBlob, [downloadUrl], errorTextId, fileName)
      window.open(downloadUrl)
      toast.success(i18next.t('common:ui.DownloadStarted'))
      logger.info(
        `Closing SignalR connection ${connection.connectionId}, reason: OK received`,
      )
      connection.stop()
    } else {
      logger.warning(
        `Closing SignalR connection ${connection.connectionId}, reason: KO received`,
      )
      connection.stop()
      errorTextId &&
        toast.error(
          i18next.t(errorTextId, {
            error: i18next.t('common:errors.CannotCreateZipFile'),
          }),
        )
    }
  })
}
